import {
  ContentServiceHttpClient,
  ConnectedContentHttpClient,
  ContentGroupServiceHttpClient,
} from '../../../common/services/httpClient';
import { GroupMoveDirection } from '../models/constants';
import { MoveAnalysisAPIRequest } from '../models/MoveAnalysisAPIRequest';
import GenericApiResponse from '../models/GenericApiResponse';
import Group from '../models/Group';
import GroupKFA from '../models/GroupKFAInterface';
import GroupAddedResponseData from '../models/GroupAddedResponseData';
import { MoveAnalysisAPIRequestKfa } from '../models/MoveAnalysisAPIRequestKfa';
import AnalysisList from '../models/AnalysisList';

const getBaseUrl = (projectId: number | string) =>
  `/projects/${projectId}/Groups`;

export const getTagsQuery = (tags: number[] | string[]): string => {
  if (tags && tags.length > 0) {
    return `?${tags.map((id) => `tagIds=${id}`).join('&')}`;
  }
  return '';
};

export const getGroups = async (
  projectId: number | string,
  tags: number[] | string[] = [],
  sortBy?: string,
  orderAsc = true
): Promise<Group[]> => {
  let sortParameter = '';
  let orderParameter = '';
  if (sortBy) {
    sortParameter = tags && tags.length === 0 ? '?' : '&';
    sortParameter += `SortBy=${sortBy}`;
    orderParameter = `&SortDescending=${String(!orderAsc)}`;
  }

  const { data } = (await ContentGroupServiceHttpClient.get(
    getBaseUrl(projectId) + getTagsQuery(tags) + sortParameter + orderParameter
  )) as GenericApiResponse<Group[]>;
  return data;
};

export const getGroupById = async (
  projectId: number | string,
  groupId: number,
  page: number,
  pageSize: number
): Promise<Group> => {
  const { data } = (await ContentGroupServiceHttpClient.get(
    `${getBaseUrl(
      projectId
    )}/${groupId}?CurrentPage=${page}&PageSize=${pageSize}`
  )) as GenericApiResponse<Group>;
  return data;
};

export const createGroup = async (
  projectId: number,
  name: string
): Promise<Group> => {
  const { data } = (await ContentGroupServiceHttpClient.post(
    getBaseUrl(projectId),
    {
      projectId,
      name,
    }
  )) as GenericApiResponse<Group>;
  return data;
};

export const updateGroupOrder = async (
  projectId: number | string,
  groupId: number | string,
  direction: GroupMoveDirection
): Promise<boolean> => {
  const { data } = (await ContentGroupServiceHttpClient.put(
    `${getBaseUrl(projectId)}/${groupId}/order`,
    {
      moveDirection: direction,
    }
  )) as GenericApiResponse<boolean>;

  return data;
};

export const updateGroupName = async (
  projectId: string,
  name: string,
  id: number | string
): Promise<boolean> => {
  const { data } = (await ContentGroupServiceHttpClient.put(
    `${getBaseUrl(projectId)}/${id}`,
    {
      projectId: parseInt(projectId, 10),
      name,
      id,
    }
  )) as GenericApiResponse<boolean>;

  return data;
};

export const deleteGroup = async (
  projectId: number | string,
  groupId: number | string
): Promise<boolean> => {
  const { data } = (await ContentGroupServiceHttpClient.delete(
    `${getBaseUrl(projectId)}/${groupId}`
  )) as GenericApiResponse<boolean>;
  return data;
};

export const getAnalysis = async (
  projectId: number,
  page: number,
  pageSize: number,
  tagIds: number[] = [],
  sortBy?: string,
  orderAsc = true
): Promise<AnalysisList> => {
  let sortParameter = '';
  let orderParameter = '';
  if (sortBy) {
    sortParameter += `&SortBy=${sortBy}`;
    orderParameter = `&SortDescending=${String(!orderAsc)}`;
  }
  if (tagIds.length > 0) {
    const { data } = (await ConnectedContentHttpClient.get(
      `${getBaseUrl(projectId)}/Analyses${getTagsQuery(
        tagIds
      )}${sortParameter}${orderParameter}`
    )) as GenericApiResponse<AnalysisList>;
    return data;
  }

  const { data } = (await ConnectedContentHttpClient.get(
    `${getBaseUrl(
      projectId
    )}/Analyses?CurrentPage=${page}&PageSize=${pageSize}${sortParameter}${orderParameter}`
  )) as GenericApiResponse<AnalysisList>;

  return data;
};

export const deleteAnalysis = async (
  projectId: number | string,
  analysisThreadId: number | string
): Promise<boolean> => {
  const { data } = (await ContentServiceHttpClient.delete(
    `/projects/${projectId}/contents/${analysisThreadId}`
  )) as GenericApiResponse<boolean>;

  return data;
};

export const deleteAnalyses = async (
  projectId: number | string,
  contentThreadIds: number[]
): Promise<boolean> => {
  const { data } = (await ContentServiceHttpClient.deleteMany(
    `/projects/${projectId}/analyses`,
    contentThreadIds
  )) as GenericApiResponse<boolean>;

  return data;
};

export const getByKFA = async (
  projectId: number,
  tags: number[] | string[] = [],
  sortBy?: string,
  orderAsc = true
): Promise<GroupKFA[]> => {
  let sortParameter = '';
  let orderParameter = '';
  if (sortBy) {
    sortParameter = tags && tags.length === 0 ? '?' : '&';
    sortParameter += `SortBy=${sortBy}`;
    orderParameter = `&SortDescending=${String(!orderAsc)}`;
  }

  const { data } = (await ConnectedContentHttpClient.get(
    `${getBaseUrl(projectId)}/KeyFocusAreas${getTagsQuery(
      tags
    )}${sortParameter}${orderParameter}`
  )) as GenericApiResponse<GroupKFA[]>;
  return data;
};

export const getAnalysisForKFA = async (
  projectId: number,
  groupId: number,
  page: number,
  pageSize: number,
  tagIds: number[] = []
): Promise<GroupKFA[]> => {
  if (tagIds?.length > 0) {
    const { data } = (await ConnectedContentHttpClient.get(
      `${getBaseUrl(projectId)}/KeyFocusAreas/${groupId}/analyses${getTagsQuery(
        tagIds
      )}`
    )) as GenericApiResponse<GroupKFA[]>;
    return data;
  }

  const { data } = (await ConnectedContentHttpClient.get(
    `${getBaseUrl(
      projectId
    )}/KeyFocusAreas/${groupId}/analyses?CurrentPage=${page}&PageSize=${pageSize}`
  )) as GenericApiResponse<GroupKFA[]>;
  return data;
};

export const updateAnalysesSortOrder = async (
  projectId: number | string,
  analysesToMove: MoveAnalysisAPIRequest[]
): Promise<boolean> => {
  const { data } = (await ContentGroupServiceHttpClient.put(
    `${getBaseUrl(projectId)}/order`,
    analysesToMove
  )) as GenericApiResponse<boolean>;

  return data;
};

export const updateAnalysesSortOrderKfa = async (
  projectId: number | string,
  analysesToMove: MoveAnalysisAPIRequestKfa[]
): Promise<boolean> => {
  const { data } = (await ContentGroupServiceHttpClient.put(
    `/projects/${projectId}/KeyFocusAreas/order`,
    analysesToMove
  )) as GenericApiResponse<boolean>;

  return data;
};

export const getAnalysesGroupById = async (
  projectId: number | string,
  groupId: number,
  page: number,
  pageSize: number
): Promise<Group> => {
  const { data } = (await ContentGroupServiceHttpClient.get(
    `${getBaseUrl(
      projectId
    )}/${groupId}?CurrentPage=${page}&PageSize=${pageSize}`
  )) as GenericApiResponse<Group>;
  return data;
};

export const getAnalysesGroupByTags = async (
  projectId: number | string,
  groupId: number,
  page: number,
  pageSize: number,
  tags: string[] = []
): Promise<Group> => {
  const tagsQuery = getTagsQuery(tags);
  const { data } = (await ContentGroupServiceHttpClient.get(
    `${getBaseUrl(
      projectId
    )}/${groupId}${tagsQuery}&CurrentPage=${page}&PageSize=${pageSize}`
  )) as GenericApiResponse<Group>;
  return data;
};

export const getAnalysesListForGroup = async (
  projectId: number | string,
  groupId: number,
  page: number,
  pageSize: number,
  tags: string[]
): Promise<Group> => {
  if (tags && tags.length > 0) {
    return getAnalysesGroupByTags(projectId, groupId, page, pageSize, tags);
  }
  return getAnalysesGroupById(projectId, groupId, page, pageSize);
};

export const createAnalysesGroup = async (
  projectId: string | number,
  name: string
): Promise<GroupAddedResponseData> => {
  const { data } = (await ContentGroupServiceHttpClient.post(
    `projects/${projectId}/groups`,
    {
      projectId,
      name,
    }
  )) as GenericApiResponse<GroupAddedResponseData>;

  return data;
};
